import { moveItemInArray } from "@angular/cdk/drag-drop";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { EMPTY, Observable, Subject as RxjsSubject, lastValueFrom } from "rxjs";
import { catchError, switchMap, take, takeUntil } from "rxjs/operators";
import { Inspection, InspectionLocation, InspectionUpdateLocation } from "../_objects/inspection";
import {
  ConflictImage,
  InspectionImage,
  InspectionImageDisplayRow,
  InspectionImageIdListItem,
  InspectionImageListItem,
  InspectionImageMultipleUpdate,
  InspectionImageOrderChange,
  InspectionImageUpdate,
  Metadata,
  MetadataPrimary,
  UploadOption
} from "../_objects/inspection-image";
import { InspectionReport, ReportLocation } from "../_objects/inspection-report";
import { MarkItem } from "../_objects/mark";
import { Subject } from "../_objects/subject";
import { SubjectLocation, SubjectModel } from "../_objects/subject-model";
import { TitleItem } from "../_objects/title";
import { UtilsService } from "../_services/_utils.service";
import { CommonSnackBarService } from "../_services/common-snack-bar.service";
import { FileSystemAccessService } from "../_services/file-system-access.service";
import { InspectionImageService } from "../_services/http-api/inspection-image.service";
import { InspectionReportService } from "../_services/http-api/inspection-report.service";
import { InspectionService } from "../_services/http-api/inspection.service";
import { SubjectService } from "../_services/http-api/subject.service";
import { TenantConfigService } from "../_services/http-api/tenant-config.service";
import { ImageUploadService } from "../_services/image-upload.service";
import { LangUpdateService } from "../_services/lang-update.service";
import { NetworkStatusService } from "../_services/network-status.service";
import { SpinnerService } from "../_services/spinner.service";
import {
  INSPECTION_DATA_MANAGEMENT___INSPECTION_DATA_PARAMS,
  INSPECTION_DATA_MANAGEMENT___INSPECTION_ID_PARAMS,
  InspectionDataQueryParam,
  StorageService
} from "../_services/storage.service";
import {
  DEFAULT_SORT_SETTING,
  IMAGE_FILETYPE,
  IMAGE_TYPE,
  INSPECTION_IMAGE_STATUS_CODE,
  INSPECTION_REPORT_STATUS_CODE,
  MODE,
  PERMISSION_CODE,
  PRINTSCREEN_ITEMS,
  PrintScreenType,
  SORT_TYPE,
  TenantConfig
} from "../app.constants";
import { CommonDeleteDialogComponent } from "../common-dialog/common-delete-dialog.component";
import { ImageConflictDialogService } from "../inspection/dialogs/image-conflict-dialog/image-conflict-dialog.service";
import { InspDataMultiUpdate, InspectionDataManageService } from "../inspection/inspection-data-manage.service";
import { ImageEditDialogComponent } from "./image-edit-dialog/image-edit-dialog.component";
import { InspectionImageEditErrorDialogComponent } from "./image-edit-dialog/inspection-image-edit-error-dialog/inspection-image-edit-error-dialog.component";
import { InspectionImageStateService } from "./inspection-images-state.service";
import { MultipleImagesEditDialogComponent } from "./multiple-images-edit-dialog/multiple-images-edit-dialog.component";
import { EvcModalService } from "../_shared/services/modal/evc-modal.service";
import { SummaryDialogComponent } from "./summary-comment/summary-comment-dialog.component";
import { LocationManagementService } from "../_services/location/location-management.service";
import { LocationStateService } from "../_services/location/location-state/location-state.service";
import { InspectionStateService } from "../_services/inspection/inspection-state.service";
import { InspectionReportStateService } from "../_services/reports/inspection-report-state.service";
import { ImagesFiltersStateService } from "../_services/filter-state.service";
import { InspectionImageFilters } from "../_objects/inspection-image-filters";
import { IconEnum } from "@evc/web-components";
import { ExternalSyncUploadService } from "../_services/image-upload/external-sync-upload.service";
import { BrianSynchronizedInspectionStateService } from "../_services/brian-synchronized-inspection-state/brian-synchronized-inspection-state.service";
import { MatSelectChange } from "@angular/material/select";
import { CommonImagesShareDialogComponent } from "../common-dialog/common-images-share-dialog/common-images-share-dialog.component";
import { UserPermissionsService } from "../_services/http-api/user-permissions.service";
import { BrianDeviceExportService, CompareImage } from "../_services/device-data-export/brian-device-export.service";
import { InspectionExportOfflineService } from "../_services/offline/inspection-export-offline.service";
import { InspectionStructureFormatService } from "../_services/inspection-structure-format.service";

@Component({
  selector: "app-inspection-data-management",
  templateUrl: "./inspection-data-management.component.html",
  styleUrls: ["./inspection-data-management.component.scss"],
  standalone: false
})
export class InspectionDataManagementComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  printScreens: PrintScreenType[];

  @Input() inspection: Inspection;
  @Input() subjectModel: SubjectModel;
  @Input() subjects: Subject[];
  @Input() marks: MarkItem[] = [];
  @Input() titles: TitleItem[];
  @Input() tags: string[];
  @Input() uploadedEvent = new Observable<boolean>();
  @Input() inspectionReport: InspectionReport;
  @Input() scrollTop: number;
  @Input() expand: boolean;
  @Input() selectedLocation: SubjectLocation;
  @Input() closedMenuWidth: number;
  @Input() openedMenuWidth: number;

  @Output() updateInspection = new EventEmitter<Inspection>();
  @Output() uploadInspectionImage = new EventEmitter<Inspection>();
  @Output() editInspectionInfo = new EventEmitter();

  inspectionSubjects: Subject[];
  inspectionImages: InspectionImageListItem[] = [];
  imagesGroupedByLocation: InspectionImageDisplayRow[];

  termLocationControl = new FormControl<SubjectLocation>(null);
  filterTermLocations: SubjectLocation[] = [];
  dropdownLocationFilter: SubjectLocation;
  filterMarkControl = new FormControl<any[]>([]);
  hasMarkFilters = false;
  sortControl = new FormControl<string>(null);
  printScreenControl = new FormControl<string>(null);
  isDataPlateSelected = false;
  sortItemsCodes: string[] = Array.from(SORT_TYPE.keys());
  isPreviousID = false;
  conflictList: ConflictImage[] = [];
  trashCanIcon = IconEnum.TrashCan;
  penToSquareIcon = IconEnum.PenToSquare;
  fileExportIcon = IconEnum.FileExport;
  brianSynchronizedInspectionId: string;
  private mouseHoverImageIndex: number;
  private lastSelectedImage: InspectionImageListItem | null = null;

  markLabel = TenantConfig.DefaultConfigurationValues.LabelOfMark;

  DEFAULT_QUERY_PARAM: InspectionDataQueryParam = {
    filter: { markId: [] },
    sort: { item: null },
    subjectImageOpen: true,
    printScreenValue: null
  };

  onDestroy$ = new RxjsSubject();

  @Output() changedReportId = new EventEmitter<string>();
  @Output() clickedReportSetting = new EventEmitter();

  get isImageSelected(): boolean {
    return this.inspDataManageService.selectedImageIds.length > 0;
  }

  get singleImageSelected(): boolean {
    return this.selectedImages.length === 1 && !this.selectedImages[0].mimeType.startsWith("video");
  }

  get isBrianMode(): boolean {
    return this.networkStatusService.isBrianMode;
  }

  get canEditImage(): boolean {
    return (
      this.inspection &&
      this.inspection.permission &&
      (this.inspection.permission & PERMISSION_CODE.WRITE) !== 0 &&
      (!this.inspectionReport ||
        this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.EDITING ||
        this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.COMPLETED ||
        this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.FAILED)
    );
  }

  get summaryComment(): string {
    if (!this.inspection || !this.selectedLocation) {
      return "";
    }

    const location = this.inspection.inspectionLocations.find((loc) => loc.identifier === this.selectedLocation.identifier);

    return location && location.comment ? location.comment : "";
  }

  constructor(
    private dialog: MatDialog,
    private cd: ChangeDetectorRef,
    private snackBar: CommonSnackBarService,
    private inspectionService: InspectionService,
    private inspectionReportService: InspectionReportService,
    private inspectionImageService: InspectionImageService,
    private subjectService: SubjectService,
    private tenantConfigService: TenantConfigService,
    private storageService: StorageService,
    private utilsService: UtilsService,
    public oscSpinnerService: SpinnerService,
    private inspDataManageService: InspectionDataManageService,
    private inspectionImageStateService: InspectionImageStateService,
    private imageUploadOnlineService: ImageUploadService,
    private networkStatusService: NetworkStatusService,
    private fileSysAccessService: FileSystemAccessService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private imageConflictDialogService: ImageConflictDialogService,
    private filterStateService: ImagesFiltersStateService,
    private evcModalService: EvcModalService,
    private locationManagementService: LocationManagementService,
    private locationStateService: LocationStateService,
    private inspectionStateService: InspectionStateService,
    private inspectionReportStateService: InspectionReportStateService,
    private elementRef: ElementRef,
    private brianSynchronizedInspectionStateService: BrianSynchronizedInspectionStateService,
    private brianExportDataService: BrianDeviceExportService,
    private externalSyncUploadService: ExternalSyncUploadService,
    private zone: NgZone,
    private userPermission: UserPermissionsService,
    private inspectionExportOfflineService: InspectionExportOfflineService,
    private structureFormatService: InspectionStructureFormatService
  ) {
    this.langService.prepare(translate, this.onDestroy$);
    this.createPrintScreenItems();
    this.setDataManageSubscribes();
    this.brianSynchronizedInspectionStateService
      .getBrianSynchronizedInspectionId$()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((inspectionId) => {
        this.brianSynchronizedInspectionId = inspectionId;
      });
  }

  ngAfterViewInit(): void {
    this.initCanEditInspection();
    this.observeLeftContentResize();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if ((changes.subjects || changes.inspection) && this.subjects && this.inspection) {
      this.inspectionSubjects = this.subjects.filter((sub) => this.inspection.subjectId === sub.id);

      if (!this.inspectionSubjects || this.inspectionSubjects.length < 1) {
        this.subjectService
          .getSubject(this.inspection.subjectId)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((subject) => (this.inspectionSubjects = [subject]));
      }
    }

    if ((changes.subjectModel || changes.selectedLocation) && this.subjectModel && this.selectedLocation) {
      this.initFilterTermLocations();
      this.initFilterForms();
      this.selectGlobalLocation(this.selectedLocation);
      this.inspDataManageService.clearSelectImageIds();
    }

    if (changes.uploadedEvent) {
      // image upload event
      this.uploadedEvent.pipe(takeUntil(this.onDestroy$)).subscribe((bool) => {
        if (bool) {
          this.loadImages();
        }
      });
    }
  }

  private initFilterTermLocations() {
    this.filterTermLocations = this.utilsService.getLocations(this.subjectModel, this.selectedLocation);
    this.locationStateService.setFilterTermLocations(this.filterTermLocations);
  }

  ngOnInit() {
    this.isPreviousID = false;
    this.isDataPlateSelected = false;
    this.setConfig();
    // inspectionIds are initialized by ngOnChanges
    this.initInspectionId(this.inspection.id);

    this.imageUploadOnlineService.uploadCompleted$.pipe(takeUntil(this.onDestroy$)).subscribe((_) => this.loadImages());

    this.inspectionImageStateService.inspectionImages$.subscribe((images) => {
      this.inspectionImages = images;
      this.groupImagesPerLocation();
      this.oscSpinnerService.hide();
      if (images.length > 0 && this.inspDataManageService.imageIdInQueryParam) {
        this.navigateToImageInUrl(images);
      }
    });
    this.externalSyncUploadService.uploadCompleted$.pipe(takeUntil(this.onDestroy$)).subscribe((_) => {
      this.zone.run(() => {
        this.loadImages(true);
      });
    });
    this.userPermission.updateUserProperties(this.inspection);
  }

  // This is needed for the Analytics Page. A User can navigate directly to an image from it's url.
  private navigateToImageInUrl(images: InspectionImageListItem[]) {
    const targetImage = images.find(
      (img) => img.id === this.inspDataManageService.imageIdInQueryParam && img.status === INSPECTION_IMAGE_STATUS_CODE.COMPLETED
    );

    if (targetImage) {
      const termLocs: SubjectLocation[] = this.utilsService.getLocations(this.subjectModel, null, IMAGE_TYPE.INSPECTIONIMAGE);
      const targetLocation = termLocs.find((location) => location.identifier === targetImage.subjectLocationId);
      this.selectGlobalLocation(targetLocation);
      this.openInspectionImageEditDialog(targetImage);
    }

    this.inspDataManageService.imageIdInQueryParam = "";
  }

  ngOnDestroy() {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
    this.snackBar.clearAll();
    this.inspDataManageService.clearState();
    this.inspectionImageStateService.clearState();
  }

  private initFilterForms() {
    const param = this.createInitQueryParam();
    this.initFilter(param.filter.markId);
    this.initSort(param.sort.item);
    this.initPrintScreen(param.printScreenValue);
  }

  private setDataManageSubscribes() {
    // Event where selected image is dropped into folder
    this.inspDataManageService
      .moveImageLocationObs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((obj) => {
        this.imageLocationMultipleUpdate(obj.location, obj.imageIds);
      });
  }

  setConfig() {
    const configs = this.tenantConfigService.tenantConfig;
    if (configs) {
      this.markLabel = configs.find((_) => _.key === TenantConfig.LabelOfMark.KEY).value as string;
    }
  }

  createInitQueryParam(): InspectionDataQueryParam {
    const param = this.storageService.fetchInspectionDataParam(INSPECTION_DATA_MANAGEMENT___INSPECTION_DATA_PARAMS);
    if (param) {
      if (!this.isPreviousID) {
        param.filter.markId = this.DEFAULT_QUERY_PARAM.filter.markId;
        param.sort.item = this.DEFAULT_QUERY_PARAM.sort.item;
        param.printScreenValue = this.DEFAULT_QUERY_PARAM.printScreenValue;
        this.storageService.pushInspectionDataParam(INSPECTION_DATA_MANAGEMENT___INSPECTION_DATA_PARAMS, {
          filter: { markId: param.filter.markId },
          sort: { item: param.sort.item },
          subjectImageOpen: param.subjectImageOpen,
          printScreenValue: param.printScreenValue
        });
      }
      return param;
    }
    return this.DEFAULT_QUERY_PARAM;
  }

  initFilter(markId: number[]) {
    let dstMarkId = markId;

    // If invalid, use default value
    if (!this.validateFilter(markId)) {
      dstMarkId = this.DEFAULT_QUERY_PARAM.filter.markId;
    }
    this.filterMarkControl.setValue(dstMarkId);
    const compareMark: number[] = [];
    for (let i = 0; i < this.marks.length; i++) {
      compareMark.push(this.marks[i].identifier);
    }
    const isNeedAll = (array1, array2) => array2.every((element) => array1.includes(element));
    if (dstMarkId.length === this.marks.length && isNeedAll(dstMarkId, compareMark)) {
      this.filterMarkControl.setValue([]);
      const markList = [];
      markList.push("all");
      for (const markData of dstMarkId) {
        markList.push(markData);
      }
    } else {
      this.filterMarkControl.setValue(dstMarkId);
    }
  }

  initSort(sortItem: string) {
    let dstSortItem = sortItem;

    // If invalid, use default value
    if (!this.validateSort(dstSortItem)) {
      dstSortItem = this.DEFAULT_QUERY_PARAM.sort.item;
    }
    this.sortControl.setValue(dstSortItem);
  }

  /** Initial settings for the PrintScreen control
   * @param printScreenValue
   */
  initPrintScreen(printScreenValue: string) {
    this.printScreenControl.setValue(printScreenValue);
  }

  validateFilter(markId: number[]): boolean {
    let result = false;
    markId.forEach((item) => {
      if (!this.utilsService.isNullOrUndefined(item) && 0 <= item && item <= this.marks?.length) {
        result = true;
      }
    });
    return result;
  }

  validateSort(sortItem: string): boolean {
    const isGetSortItem = this.sortItemsCodes.some((item) => {
      return sortItem === item;
    });
    return !(!this.utilsService.isNullOrUndefined(sortItem) && !isGetSortItem);
  }

  initInspectionId(currentInspectionId: string) {
    const param = this.storageService.fetchInspectionIdParam(INSPECTION_DATA_MANAGEMENT___INSPECTION_ID_PARAMS);

    const idsMatchParam =
      param && [currentInspectionId].length === param.inspectionId.length && [currentInspectionId].every((id) => param.inspectionId.includes(id));

    if (!param || !idsMatchParam) {
      this.storageService.pushInspectionIdParam(INSPECTION_DATA_MANAGEMENT___INSPECTION_ID_PARAMS, { inspectionId: [currentInspectionId] });
    } else {
      this.isPreviousID = true;
    }
  }

  createPrintScreenItems() {
    this.printScreens = PRINTSCREEN_ITEMS;
  }

  selectGlobalLocation(location: SubjectLocation) {
    const tempLocation = location ? this.getSubjectLocation(location, this.subjectModel.locations) : null;
    this.selectedLocation = tempLocation;
    if (!tempLocation || tempLocation?.children.length > 0) {
      // Select node
      this.termLocationControl.setValue(null);
      this.dropdownLocationFilter = null;
    } else {
      // Termination selection
      this.termLocationControl.setValue(tempLocation);
      this.dropdownLocationFilter = tempLocation;
    }

    this.isDataPlateSelected = location && location.isDataPlate;
    this.locationStateService.setIsDataPlateSelected(this.isDataPlateSelected);
    this.initializeLocationFilters(tempLocation);

    this.loadImages();
  }

  private initializeLocationFilters(location: SubjectLocation) {
    this.initFilterLocation(location);
    this.initFilterTermLocations();
    this.inspDataManageService.filterLocationEmit(location);
  }

  private initFilterLocation(tempLocation: SubjectLocation) {
    const filterLocation = this.locationManagementService.getPrimaryLocation(tempLocation?.identifier, this.subjectModel);
    this.locationStateService.setFilterLocation(filterLocation);
  }

  getSubjectLocation(target: SubjectLocation, locations: SubjectLocation[]): SubjectLocation {
    let result: SubjectLocation = null;
    locations.some((location) => {
      if (location.identifier === target.identifier) {
        result = location;
        return true;
      } else if (location.children.length > 0) {
        result = this.getSubjectLocation(target, location.children);
        return result !== null;
      }
    });
    return result;
  }

  changeLocationFilter(change: MatSelectChange) {
    if (change.value === undefined) {
      this.selectGlobalLocation(null);
    }
    if (this.dropdownLocationFilter !== change.value) {
      this.selectSecondaryLocationFilter(change.value);
      this.inspDataManageService.filterLocationEmit(change.value);
    }
  }

  selectSecondaryLocationFilter(location: SubjectLocation) {
    this.dropdownLocationFilter = location;
    this.isDataPlateSelected = location && location.isDataPlate;
    this.locationStateService.setIsDataPlateSelected(this.isDataPlateSelected);
    this.loadImages();
  }

  /**
   * Print screen 㝮靸択値を変更㝙る。
   *
   * @param printScreenType PrintScreenType。
   */
  public changePrintScreenFilter(printScreenType: PrintScreenType): void {
    if (this.printScreenControl.value === printScreenType.value) {
      return;
    }

    this.printScreenControl.setValue(printScreenType.value);
    this.storageService.pushInspectionDataParam(INSPECTION_DATA_MANAGEMENT___INSPECTION_DATA_PARAMS, this.getImageFilterValues());
    this.loadImages();
  }

  /**
   * Get all image filters
   * @returns InspectionDataQueryParam
   */
  getImageFilterValues(): InspectionDataQueryParam {
    return {
      filter: { markId: this.filterMarkControl.value.filter((markId) => typeof markId === "number") },
      sort: { item: this.sortControl.value },
      subjectImageOpen: true,
      printScreenValue: this.printScreenControl.value
    };
  }

  public trackByImageItem(index: number, item: InspectionImageListItem) {
    return item.id;
  }

  loadImages(inBackground = false) {
    if (!inBackground) {
      this.oscSpinnerService.show();
    }
    this.filterStateService.inspectionImageFilters = this.createFiltersForUpdate();
    this.hasMarkFilters = this.filterStateService.inspectionImageFilters.marks?.length > 0;
    this.inspectionImageStateService.fetchAndUpdate(this.inspection.id);
  }

  createFiltersForUpdate(): InspectionImageFilters {
    const filterMarkList = this.filterMarkControl.value?.filter((_) => _ !== "all");
    return {
      subjectLocation: this.selectedLocation,
      marks: filterMarkList,
      sort: DEFAULT_SORT_SETTING.SORT,
      sortOrder: DEFAULT_SORT_SETTING.SORTORDER,
      printScreens: this.printScreenControl.value || this.printScreenControl.value === "0" ? [this.printScreenControl.value] : []
    };
  }

  // =================================================================================
  selectLocation4Report(location: SubjectLocation, post: boolean, imageSelected: InspectionImageListItem[]) {
    if (!this.canEditInspection()) {
      return;
    }
    if (location) {
      this.inspectionReport = this.locationManagementService.toggleLocationInReport(location, this.inspectionReport, imageSelected);
      this.inspectionReportStateService.setInspectionReport(this.inspectionReport);
      if (post) {
        this.locationManagementService.patchInspectionReportLocations(this.inspectionReport);
      }
    }
  }

  selectImage4Report(inspectionImage: InspectionImageListItem, post: boolean, onMovingFolder: boolean = false) {
    if (!this.isSelectedImage4Report(inspectionImage)) {
      // add
      const loc: SubjectLocation = this.locationManagementService.getTerminateLocation(inspectionImage.subjectLocationId, this.subjectModel);
      if (!this.locationManagementService.isSelectedLocationForReport(loc, this.inspectionReport) && !onMovingFolder) {
        this.selectLocation4Report(loc, false, [inspectionImage]);
      }
      this.inspectionReport.reportLocations.forEach((location) => {
        if (location.identifier === inspectionImage.subjectLocationId && location.imageIds) {
          location.imageIds.push(inspectionImage.id);
        }
      });
    } else {
      // delete
      this.inspectionReport.reportLocations.forEach((location) => {
        if (location.imageIds) {
          location.imageIds = location.imageIds.filter((imageId) => {
            return imageId !== inspectionImage.id;
          });
        }
      });
    }
    if (post) {
      this.locationManagementService.patchInspectionReportLocations(this.inspectionReport);
    }
    this.cd.detectChanges();
  }

  selectImage4Select(event: { image: InspectionImageListItem; shiftKey: boolean }) {
    if (!this.canEditInspection()) {
      return;
    }

    const inspectionImage = event.image;

    if (event.shiftKey && this.lastSelectedImage) {
      this.selectImageRange(this.lastSelectedImage, inspectionImage);
      return;
    }

    if (!this.isSelectedImage4Select(inspectionImage)) {
      // add
      const tempImageIdListItem = new InspectionImageIdListItem();
      tempImageIdListItem.id = inspectionImage.id;
      tempImageIdListItem.locationId = inspectionImage.subjectLocationId;
      tempImageIdListItem.status = inspectionImage.status;
      this.inspDataManageService.selectImageId(tempImageIdListItem);
      this.lastSelectedImage = inspectionImage;
    } else {
      // delete
      this.inspDataManageService.deselectImageId(inspectionImage.id);
      this.lastSelectedImage = null;
    }
  }

  selectImageAll4Report(event: any) {
    event.stopPropagation();

    if (!this.isSelectedImageAll4Report()) {
      // add all
      this.inspectionImages
        .filter((inspectionImage) => {
          return this.hasLocations(inspectionImage.subjectLocationId) || inspectionImage.isDataPlate;
        })
        .forEach((inspectionImage) => {
          if (!this.isSelectedImage4Report(inspectionImage)) {
            this.selectImage4Report(inspectionImage, false);
          }
        });
    } else {
      // delete all
      this.inspectionReport.reportLocations.forEach((location) => {
        if (location.imageIds) {
          location.imageIds = location.imageIds.filter((imageId) => {
            const targetImage = this.inspectionImages.find((inspectionImage) => {
              return imageId === inspectionImage.id;
            });
            return !targetImage;
          });
        }
      });
    }

    this.locationManagementService.patchInspectionReportLocations(this.inspectionReport);
  }

  selectImageAll4Select() {
    if (!this.isSelectedImageAll4Select()) {
      // add all
      this.inspectionImages.forEach((inspectionImage) => {
        if (!this.isSelectedImage4Select(inspectionImage)) {
          this.selectImage4Select({ image: inspectionImage, shiftKey: false });
        }
      });
    } else {
      // delete all
      this.inspDataManageService.clearSelectImageIds();
    }
  }

  /** 靸択㝕れ㝟レポート㝮ステータスを確誝
   * @returns boolean
   */
  validateStatus(): boolean {
    return (
      this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.EDITING || this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.FAILED
    );
  }

  isSelectedImageAll4Report(): boolean {
    let result = true;
    if (0 < this.getNumberOfStillInspectionImage()) {
      this.inspectionImages
        .filter((inspectionImage) => {
          return this.hasLocations(inspectionImage.subjectLocationId) || inspectionImage.isDataPlate;
        })
        .forEach((inspectionImage) => {
          if (!this.isSelectedImage4Report(inspectionImage)) {
            result = false;
          }
        });
    } else {
      result = false;
    }
    return result;
  }

  isDataPlateOnly(): boolean {
    let isDataPlateOnly = true;
    if (this.inspectionImages) {
      this.inspectionImages.forEach((img) => {
        if (!img.isDataPlate) {
          isDataPlateOnly = false;
        }
      });
    }
    return isDataPlateOnly;
  }

  /**
   *
   * @returns
   * false: display "Select All for Edit".
   * true: display "Deselect All for Edit".
   */
  isSelectedImageAll4Select(): boolean {
    let result = true;
    if (0 < this.getNumberOfStillInspectionImage()) {
      this.inspectionImages
        .filter((inspectionImage) => {
          if (this.isDataPlateSelected) {
            return true;
          }
          return !inspectionImage.subjectLocationId || !inspectionImage.isDataPlate;
        })
        .forEach((inspectionImage) => {
          if (!this.isSelectedImage4Select(inspectionImage)) {
            result = false;
          }
        });
    } else {
      result = false;
    }
    return result;
  }

  isSelectedImage4Report(inspectionImage: InspectionImageListItem): boolean {
    if (this.inspectionReport) {
      for (const subjectLocation of this.inspectionReport.reportLocations) {
        if (subjectLocation.imageIds) {
          for (const imageId of subjectLocation.imageIds) {
            if (imageId === inspectionImage.id) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  isSelectedImage4Select(inspectionImage: InspectionImageListItem): boolean {
    return this.inspDataManageService.selectedImageIds.some((img) => img.id === inspectionImage.id);
  }

  isContainDataplateImageSelected(): boolean {
    if (!this.inspectionImages) {
      return false;
    }

    const selectedImages = this.inspectionImages.filter((image) =>
      this.inspDataManageService.selectedImageIds.some((selectedImage) => image.id === selectedImage.id)
    );

    // DataPlate㝌坫㝾れ㝦㝄㝪㝄㝓㝨を検証
    return selectedImages.some((selectedImage) => selectedImage.subjectLocationId && this.isDataPlate(selectedImage.subjectLocationId));
  }

  isContainUploadingImageSelected(): boolean {
    if (!this.inspectionImages) {
      return false;
    }

    const selectedImages = this.inspectionImages.filter((image) =>
      this.inspDataManageService.selectedImageIds.some((selectedImage) => image.id === selectedImage.id)
    );

    // Status=Uploading㝌坫㝾れ㝦㝄㝪㝄㝓㝨を検証
    return selectedImages.some((selectedImage) => selectedImage.status === INSPECTION_IMAGE_STATUS_CODE.UPLOADING);
  }

  selectPrimaryLocation4Report(primLoc: SubjectLocation, inspectionReport: InspectionReport, subjectModel: SubjectModel) {
    if (!this.canEditInspection()) {
      return;
    }

    this.locationManagementService.selectPrimaryLocationForReport(primLoc, inspectionReport, subjectModel);
  }

  // ----------------------------------------------------------------------------------

  getNumberOfStillInspectionImage(): number {
    return this.inspectionImages?.length ?? 0;
  }

  isStillInspectionImage(inspectionImage: InspectionImageListItem) {
    return inspectionImage.isDataPlate || this.hasLocations(inspectionImage.subjectLocationId);
  }

  isDataPlate(subjectLocationId: string): boolean {
    let isDataPlate = false;
    const termLocs: SubjectLocation[] = this.utilsService.getLocations(this.subjectModel, null, IMAGE_TYPE.INSPECTIONIMAGE);
    termLocs.forEach((location) => {
      if (location.identifier === subjectLocationId && location.isDataPlate) {
        isDataPlate = true;
      }
    });
    return isDataPlate;
  }

  hasLocations(subjectLocationId: string): boolean {
    let hasLocations = false;
    const termLocs: SubjectLocation[] = this.utilsService.getLocations(this.subjectModel, null, IMAGE_TYPE.INSPECTIONIMAGE);
    termLocs.forEach((location) => {
      if (location.identifier === subjectLocationId && !location.isDataPlate) {
        hasLocations = true;
      }
    });
    return hasLocations;
  }

  isContainLocations(): boolean {
    let isContainLocation = false;
    this.inspectionImages.forEach((image) => {
      if (image.subjectLocationId !== null) {
        isContainLocation = true;
      }
    });
    return isContainLocation;
  }

  // =================================================================================

  openInspectionImageEditDialog(inspectionImageListItem: InspectionImageListItem) {
    this.snackBar.clearAll();
    const listIndex = this.inspectionImages.findIndex((image) => image.id === inspectionImageListItem.id);

    const dialogRef = this.evcModalService.showModal(ImageEditDialogComponent, {
      inspectionImage: inspectionImageListItem,
      subjectModel: this.subjectModel,
      images: this.inspectionImages,
      index: listIndex,
      isEditable: this.canEditImage,
      inspectionReport: this.inspectionReport,
      applyImage: this.loadImages.bind(this),
      applyReport: (result: any) => {
        this.loadImages();
        this.updateReportDisplay(result.updatedReports);
      },
      changeReportLocation: (result: any) => {
        if (this.inspectionReport && result !== undefined) {
          this.inspectionReport.reportLocations = result.reportLocations;
        }
      }
    });

    dialogRef.afterClosed.pipe(take(1)).subscribe(this.loadImages.bind(this));
  }

  updateReportDisplay(updatedReports: any[]) {
    if (this.inspectionReport && this.inspectionReport.id) {
      for (const report of updatedReports) {
        if (report.id === this.inspectionReport.id) {
          this.inspectionReportService.getInspectionReport(this.inspectionReport.reportId).subscribe((data) => {
            this.inspectionReport.reportLocations = data.reportLocations;
            this.inspectionReportStateService.setInspectionReport(this.inspectionReport);
          });
        }
      }
    }
  }

  deleteImageDisplay(updatedReports: any[]) {
    this.loadImages();
    this.updateReportDisplay(updatedReports);
  }

  isReportStatusEditing(reportStatus): boolean {
    return this.utilsService.isReportStatusEditing(reportStatus) || this.utilsService.isReportStatusFailed(reportStatus);
  }

  canEditInspection(): boolean {
    return !(!this.canEditImage || !this.inspection.id);
  }

  openEditSectionCommentDialog() {
    this.evcModalService
      .showModal(SummaryDialogComponent, {
        locationName: this.selectedLocation?.name,
        comment: this.summaryComment
      })
      .afterClosed.pipe(take(1))
      .subscribe(async (comment: string | undefined) => {
        if (comment === undefined) {
          return;
        }

        const trimmedComment = comment.trim();
        const hadInitialComment = !!this.summaryComment;

        if (hadInitialComment || trimmedComment.length > 0) {
          await this.updateSectionComment(trimmedComment);
        }
      });
  }

  isUnfiltered(): boolean {
    const filterValue = this.getImageFilterValues();
    return (
      (filterValue.filter.markId === null || filterValue.filter.markId === undefined || filterValue.filter.markId.length <= 0) &&
      (filterValue.printScreenValue === null || filterValue.printScreenValue === "All")
    );
  }

  initCanEditInspection(): void {
    this.inspectionStateService.setCanEditInspection(this.canEditInspection());
  }

  async imagePropertyMultipleUpdate(multiUpdateData: InspDataMultiUpdate, dialogRef: MatDialogRef<MultipleImagesEditDialogComponent>) {
    this.oscSpinnerService.show(true);
    const inspectionImageMultipleUpdate = new InspectionImageMultipleUpdate();
    inspectionImageMultipleUpdate.imageIds = this.selectedImages;
    inspectionImageMultipleUpdate.subjectLocationId = null;
    inspectionImageMultipleUpdate.comment = multiUpdateData.isNoChangeComment ? null : multiUpdateData.comment;

    const metadataPrimary = new MetadataPrimary();
    metadataPrimary.mark = multiUpdateData.mark;
    metadataPrimary.title = multiUpdateData.title;
    metadataPrimary.tags = multiUpdateData.tags;
    inspectionImageMultipleUpdate.metadata = metadataPrimary;

    let conflictList: any;
    let response: any;
    const inspImages = this.inspectionImages.map((_) => {
      _.usedImageModeControl = null;
      return _;
    });
    this.inspectionImageService.patchInspectionImageMultiple(this.inspection.id, inspectionImageMultipleUpdate).subscribe(
      (data) => {
        response = data;
        conflictList = data.conflict;
      },
      (error) => {
        this.snackBar.open(this.translate.instant("inspectionDataManagement.imagePropertyMultipleUpdateError"));
        console.log(error);
        this.loadImages();
        this.cd.detectChanges();
        this.oscSpinnerService.hide(true);
      },
      () => {
        if (response.error && response.error.length === 0) {
          this.loadImages();
          if (response.updatedReports.length !== 0) {
            this.updateReportDisplay(response.updatedReports);
          }

          this.snackBar.open(this.translate.instant("inspectionDataManagement.imagePropertyMultipleUpdateComplete"));
          this.loadImages();
          dialogRef.close();
          this.inspDataManageService.clearSelectImageIds();

          if (conflictList.length !== 0) {
            conflictList.forEach((conflict) => {
              this.inspectionImageService.getInspectionImage(this.inspection.id, conflict.id).subscribe(
                (data) => {
                  const req = this.inspectionImageService.convertInspectionImage2InpsctionImageUpdate(data);
                  req.comment = inspectionImageMultipleUpdate.comment ?? data.comment;
                  req.metadata.mark = multiUpdateData.mark ?? data.metadata.mark;
                  req.metadata.title = multiUpdateData.title ?? data.metadata.title;
                  req.metadata.tags = multiUpdateData.tags ?? data.metadata.tags;
                  req.uploadOption = UploadOption.None;

                  const originalPath = inspImages.find((_) => _.id === conflict.id)?.thumbFilePath;
                  const conflictPath = inspImages.find(
                    (_) => _.id !== conflict.id && _.fileName === conflict.fileName && _.subjectLocationId === conflict.subjectLocationId
                  )?.thumbFilePath;
                  this.conflictList.push(new ConflictImage(null, conflict.fileName, null, req, originalPath, conflictPath, false));

                  if (!this.imageConflictDialogService.isOpened) {
                    this.openConflictDialog(response, inspectionImageMultipleUpdate);
                  }
                },
                (error) => {},
                () => {
                  this.inspDataManageService.clearSelectImageIds();
                  this.snackBar.open(this.translate.instant("inspectionDataManagement.imagePropertyMultipleUpdateComplete"));
                }
              );
            });
          }
        } else {
          this.openErrorDialog(response, false);
          this.oscSpinnerService.hide(true);
        }
        this.loadImages();
      }
    );
  }

  imageLocationMultipleUpdate(location: SubjectLocation, imageIds: InspectionImageIdListItem[]) {
    this.oscSpinnerService.show();

    const inspectionImageMultipleUpdate = new InspectionImageMultipleUpdate();
    inspectionImageMultipleUpdate.imageIds = this.selectedImages;
    inspectionImageMultipleUpdate.subjectLocationId = location.identifier;
    inspectionImageMultipleUpdate.comment = null;
    inspectionImageMultipleUpdate.metadata = new Metadata();
    inspectionImageMultipleUpdate.metadata.mark = null;
    inspectionImageMultipleUpdate.metadata.title = null;
    inspectionImageMultipleUpdate.metadata.tags = null;

    let conflictList: any;
    let response: any;
    const inspImages = this.inspectionImages.map((_) => {
      _.usedImageModeControl = null;
      return _;
    });
    this.inspectionImageService.patchInspectionImageMultiple(this.inspection.id, inspectionImageMultipleUpdate).subscribe(
      (data) => {
        response = data;
        conflictList = data.conflict;
      },
      (error) => {
        this.snackBar.open(this.translate.instant("inspectionDataManagement.imageLocationMultipleUpdateError"));
        console.log(error);
        this.oscSpinnerService.hide();
      },
      () => {
        if (response.error && response.error.length === 0) {
          if (conflictList.length !== 0) {
            conflictList.forEach((conflict) => {
              this.inspectionImageService.getInspectionImage(this.inspection.id, conflict.id).subscribe(
                (data) => {
                  const req = this.inspectionImageService.convertInspectionImage2InpsctionImageUpdate(data);
                  req.subjectLocationId = location.identifier;

                  const originalPath = inspImages.find((_) => _.id === conflict.id)?.thumbFilePath;
                  const conflictPath = inspImages.find(
                    (_) => _.id !== conflict.id && _.fileName === conflict.fileName && location.identifier === conflict.subjectLocationId
                  )?.thumbFilePath;
                  this.conflictList.push(new ConflictImage(null, conflict.fileName, null, req, originalPath, conflictPath, false));

                  if (!this.imageConflictDialogService.isOpened) {
                    this.openConflictDialog(response, inspectionImageMultipleUpdate);
                  }
                },
                (error) => {},
                () => {
                  this.inspDataManageService.clearSelectImageIds();
                  this.snackBar.open(this.translate.instant("inspectionDataManagement.imagePropertyMultipleUpdateComplete"));
                }
              );
            });
          } else {
            const imagesToUpdate = this.inspectionImages.filter((img) => response.success.some((successItem) => successItem.id === img.id));
            const loc: SubjectLocation = this.locationManagementService.getTerminateLocation(
              inspectionImageMultipleUpdate.subjectLocationId,
              this.subjectModel
            );

            imagesToUpdate.forEach((img) => {
              if (this.inspectionReport != null) {
                this.inspectionReport.reportLocations.forEach((location) => {
                  // Avoid the images to remain in two different locations when they are moved
                  this.updateLocationImageMapping(location, img, inspectionImageMultipleUpdate.subjectLocationId);
                });
                img.subjectLocationId = inspectionImageMultipleUpdate.subjectLocationId;

                const isImageSelected = this.isSelectedImage4Report(img);
                const isLocationSelected = this.locationManagementService.isSelectedLocationForReport(loc, this.inspectionReport);

                if (isLocationSelected != isImageSelected) {
                  this.selectImage4Report(img, true, true);
                } else {
                  this.locationManagementService.patchInspectionReportLocations(this.inspectionReport);
                }
              }
            });
            this.inspDataManageService.clearSelectImageIds();
            this.snackBar.open(this.translate.instant("inspectionDataManagement.imagePropertyMultipleUpdateComplete"));
          }
        } else {
          this.updateImagesOfDisplay(response, inspectionImageMultipleUpdate.subjectLocationId);
          this.openErrorDialog(response, false);
        }

        this.loadImages();
      }
    );
  }

  private updateLocationImageMapping(location: ReportLocation, img: InspectionImageListItem, subjectLocationId: string) {
    if (location.identifier === subjectLocationId) {
      if (!location.imageIds.includes(img.id)) {
        location.imageIds.push(img.id);
      }
    } else {
      location.imageIds = location.imageIds.filter((imageId) => imageId !== img.id);
    }
  }

  updateImagesOfDisplay(response: any, subjectLocationId: string) {
    response.success.forEach((successImage) => {
      this.inspectionImages.forEach((img) => {
        if (img.id === successImage.id) {
          img.sortOrder = null;
        }
      });
    });
    response.success.forEach((successImage) => {
      const inspectionImageListItem: InspectionImageListItem = this.inspectionImages.find((image) => {
        return image.id === successImage.id;
      });
      const tempInspectionImage: InspectionImage = new InspectionImage();
      tempInspectionImage.id = inspectionImageListItem.id;
      tempInspectionImage.inspectionId = inspectionImageListItem.inspectionId;
      tempInspectionImage.imageDescription = inspectionImageListItem.imageDescription;
      tempInspectionImage.datetime = inspectionImageListItem.datetime;
      tempInspectionImage.mimeType = inspectionImageListItem.mimeType;
      tempInspectionImage.metadata = new Metadata();
      tempInspectionImage.status = inspectionImageListItem.status;
      tempInspectionImage.orientation = inspectionImageListItem.orientation;
      if (!subjectLocationId || !this.isDataPlate(subjectLocationId)) {
        tempInspectionImage.metadata.title = inspectionImageListItem.title;
        tempInspectionImage.metadata.tags = inspectionImageListItem.tags;
        tempInspectionImage.metadata.mark = inspectionImageListItem.mark;
        tempInspectionImage.comment = inspectionImageListItem.comment;
        tempInspectionImage.fileName = inspectionImageListItem.fileName;
      } else {
        tempInspectionImage.metadata.title = null;
        tempInspectionImage.metadata.mark = null;
        tempInspectionImage.metadata.tags = null;
        tempInspectionImage.comment = null;
        // filename update
        const marker: string[] = ["_A", "_B", "_C", "_D", "_E", "_F", "_G"];
        const fileName = inspectionImageListItem.fileName;
        const extension = fileName.substring(fileName.lastIndexOf("."), fileName.length);
        const filemark = fileName.substring(fileName.lastIndexOf(".") - 2, fileName.lastIndexOf("."));
        const fileNameNoExt =
          marker.indexOf(filemark) >= 0 ? fileName.substring(0, fileName.lastIndexOf(".") - 2) : fileName.substring(0, fileName.lastIndexOf("."));
        tempInspectionImage.fileName = fileNameNoExt + extension;
        tempInspectionImage.isDataPlate = this.isDataPlate(subjectLocationId);
      }
      // display update
      tempInspectionImage.thumbFilePath = inspectionImageListItem.thumbFilePath;
      tempInspectionImage.subjectLocationId = subjectLocationId;
      this.loadImages();
    });
    if (response.updatedReports.length !== 0) {
      this.updateReportDisplay(response.updatedReports);
    }
  }

  private get selectedImages() {
    return this.inspectionImages.filter((image) => this.inspDataManageService.selectedImageIds.find((selected) => selected.id == image.id));
  }

  openMultipleDeleteDialog(multipleEditDialog?: MatDialogRef<MultipleImagesEditDialogComponent>) {
    const fileNames = this.inspDataManageService.selectedImageIds.map(
      (selectItem) => this.inspectionImages.find((image) => image.id === selectItem.id).fileName
    );
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
      width: "600px",
      minWidth: "400px",
      maxHeight: "900px",
      data: {
        target: this.translate.instant("inspectionDataManagement.openMultipleDeleteDialogTarget"),
        deleteNames: fileNames
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.imageMultipleDelete(multipleEditDialog);
      }
    });
  }

  public deleteSingleImage(inspectionImage: InspectionImageListItem) {
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
      width: "600px",
      minWidth: "400px",
      maxHeight: "800px",
      data: {
        target: this.translate.instant("imageEditDialog.deleteDialogTarget"),
        deleteNames: [inspectionImage.fileName]
      }
    });
    dialogRef.afterClosed().subscribe((isDelete) => {
      if (!isDelete) {
        return;
      }

      this.oscSpinnerService.show();
      this.inspectionImageService.deleteInspectionImage(this.inspection.id, inspectionImage.id).subscribe({
        next: () => {
          this.snackBar.open(this.translate.instant("imageEditDialog.deleteComplete"));
        },
        error: (error) => {
          this.snackBar.open(this.translate.instant("imageEditDialog.deleteError"));
        }
      });
    });
  }

  openMultipleImageEditDialog() {
    const dialogRef = this.dialog.open(MultipleImagesEditDialogComponent, {
      width: "400px",
      height: "570px",
      autoFocus: false,
      data: {
        selectedImages: this.selectedImages,
        inspections: [this.inspection],
        marks: this.marks,
        titles: this.titles,
        tags: this.tags
      }
    });
    dialogRef.componentInstance.updateEvent.pipe(takeUntil(this.onDestroy$)).subscribe((inspDataMultiUpdate) => {
      this.loadImages();
      this.imagePropertyMultipleUpdate(inspDataMultiUpdate, dialogRef);
    });
    dialogRef.componentInstance.deleteEvent.pipe(takeUntil(this.onDestroy$)).subscribe((_) => this.openMultipleDeleteDialog(dialogRef));
  }

  private openConflictDialog(response: any, inspectionImageMultipleUpdate: InspectionImageMultipleUpdate): void {
    const dialogRef = this.imageConflictDialogService.open(this.conflictList);

    dialogRef.componentInstance.dialogAction.subscribe((conflictAction) => {
      if (conflictAction.applyToAll) {
        this.conflictList.forEach((conflictFile) => {
          this.changeImageFolder(conflictFile, conflictAction.uploadOption);
        });
        this.conflictList = [];
        this.imageConflictDialogService.close();
      } else {
        this.oscSpinnerService.show(true);
        this.imageConflictDialogService.nextConflictImage();

        conflictAction.uploadOption !== UploadOption.Skip
          ? this.changeImageFolder(conflictAction.image, conflictAction.uploadOption)
          : this.oscSpinnerService.hide(true);

        if (this.imageConflictDialogService.conflictImageList.length == 0) {
          this.updateImagesOfDisplay(response, inspectionImageMultipleUpdate.subjectLocationId);
          this.inspDataManageService.clearSelectImageIds();
          this.loadImages();
          this.imageConflictDialogService.close();
          this.oscSpinnerService.hide(true);
        }
      }
    });

    dialogRef.componentInstance.cancelAction.subscribe(() => {
      inspectionImageMultipleUpdate.subjectLocationId = null;
      this.updateImagesOfDisplay(response, inspectionImageMultipleUpdate.subjectLocationId);
      this.imageConflictDialogService.close();
      this.conflictList = [];
    });
  }

  private changeImageFolder(conflictImage: ConflictImage, uploadOption: UploadOption) {
    conflictImage.inspectionImageCreate.uploadOption = uploadOption;
    const req = conflictImage.inspectionImageCreate as InspectionImageUpdate;
    if (this.isDataPlate(req.subjectLocationId)) {
      req.metadata.mark = null;
      req.metadata.title = null;
      req.metadata.tags = null;
      req.comment = null;
      req.isDataPlate = true;
    }
    this.inspectionImageService.patchInspectionImage(req).subscribe(
      (data) => {
        this.loadImages();
      },
      (error) => {
        this.snackBar.open(this.translate.instant("imageEditDialog.submitError"));
        this.oscSpinnerService.hide();
        console.log(error);
      },
      () => {
        if (this.conflictList.length === 0) {
          this.loadImages();
        }
      }
    );
  }

  imageMultipleDelete(multipleEditDialog?: MatDialogRef<MultipleImagesEditDialogComponent>) {
    this.oscSpinnerService.show(true);
    this.inspectionImageService
      .deleteInspectionImageMultiple(
        this.inspection.id,
        this.inspDataManageService.selectedImageIds.map((selectItem) => selectItem.id)
      )
      .subscribe(
        (response) => {
          if (response.error && response.error.length === 0) {
            this.deleteImageDisplay(response.updatedReports);
            if (multipleEditDialog) {
              multipleEditDialog.close();
            }
            this.snackBar.open(this.translate.instant("inspectionDataManagement.imageMultipleDeleteComplete"));
          } else {
            this.inspDataManageService.selectedImageIds.forEach((selectItem) => {
              if (response.error.indexOf(selectItem.id) === -1) {
                this.deleteImageDisplay(response.updatedReports);
              }
            });
            this.openErrorDialog(response, true);
          }

          this.loadImages();
          this.inspDataManageService.clearSelectImageIds();
          this.oscSpinnerService.hide(true);
        },
        (error) => {
          this.snackBar.open(this.translate.instant("inspectionDataManagement.imageMultipleDeleteError"));
          this.oscSpinnerService.hide(true);
          console.log(error);
        }
      );
  }

  openErrorDialog(response: any, isDelete: boolean) {
    const failedFileNameList: string[] = [];
    if (response.error) {
      response.error.forEach((errorid) => {
        const inspectionImageListItem: InspectionImageListItem = this.inspectionImages.find((image) => {
          return image.id === errorid;
        });
        if (inspectionImageListItem) {
          failedFileNameList.push(inspectionImageListItem.fileName);
        }
      });
      if (!isDelete) {
        this.dialog.open(InspectionImageEditErrorDialogComponent, {
          width: "500px",
          height: "400px",
          disableClose: true,
          data: {
            successFileCount: response.success.length,
            fileCount: response.success.length + response.error.length,
            failedFileNameList: failedFileNameList
          }
        });
      } else {
        this.dialog.open(InspectionImageEditErrorDialogComponent, {
          width: "500px",
          height: "400px",
          disableClose: true,
          data: {
            successFileCount: this.inspDataManageService.selectedImageIds.length - response.error.length,
            fileCount: this.inspDataManageService.selectedImageIds.length,
            failedFileNameList: failedFileNameList
          }
        });
      }
    } else {
      this.snackBar.open(this.translate.instant("inspectionDataManagement.openErrorDialog"));
    }
  }

  changeOrder(locIndex: number, locationId: string) {
    this.cd.reattach();
    if (!this.canChangeOrder(locationId)) {
      return;
    }

    const images = this.imagesGroupedByLocation[locIndex].images;
    this.inspDataManageService.selectedImageIds.forEach((selected) => {
      const previousImageIndex = images.findIndex((image) => image.id == selected.id);
      moveItemInArray(images, previousImageIndex, this.mouseHoverImageIndex);
    });

    this.oscSpinnerService.show();
    const imagesOrderRequest = images.map((i, index) => ({ ...i, sortOrder: index + 1 }));
    this.inspectionImageService
      .patchInspectionImageOrderChange(this.inspection.id, new InspectionImageOrderChange(this.inspection.id, locationId, imagesOrderRequest))
      .pipe(
        catchError((e) => {
          this.inspectionImageStateService.fetchAndUpdate(this.inspection.id);
          this.snackBar.open(this.translate.instant("inspectionDataManagement.imageLocationMultipleUpdateError"));
          return EMPTY;
        })
      )
      .subscribe((_) => this.inspectionImageStateService.fetchAndUpdate(this.inspection.id));
  }

  onDragStart() {
    // we need this because of performance issues with angular change detection strategy when dragging elements
    this.cd.detach();
  }

  onDragEnd() {
    // we need this because of performance issues with angular change detection strategy when dragging elements
    this.cd.reattach();
    this.cd.detectChanges();
  }

  onDragOver(index: number, event: Event) {
    this.mouseHoverImageIndex = index;
    event.preventDefault();
  }

  canChangeOrder(subjectLocationId: string): boolean {
    return this.isImageSelected && this.selectedImages.every((i) => i.subjectLocationId === subjectLocationId);
  }

  async updateSectionComment(comment: string) {
    if (this.inspection.inspectionLocations === undefined) {
      console.error("inspections has not inspectionLocation");
      return;
    }

    this.oscSpinnerService.show();

    const index = this.inspection.inspectionLocations.findIndex((location) => location.identifier === this.selectedLocation?.identifier);

    if (index > -1) {
      this.inspection.inspectionLocations[index].comment = comment;
    } else {
      this.inspection.inspectionLocations.push(new InspectionLocation(this.selectedLocation?.identifier, comment));
    }

    const inspection = new InspectionUpdateLocation(this.inspection.id, this.inspection.inspectionLocations);

    try {
      this.inspection = await lastValueFrom(this.inspectionService.updateInspectionLocations(inspection));
      this.updateInspection.emit(this.inspection);
      this.snackBar.open(this.translate.instant("inspectionDataManagement.updateSummaryCommentComplete"));
    } catch (error) {
      this.snackBar.open(this.translate.instant("inspectionDataManagement.updateSummaryCommentError"));
    } finally {
      this.oscSpinnerService.hide();
    }
  }

  private selectImageRange(fromImage: InspectionImageListItem, toImage: InspectionImageListItem) {
    const flatImages = this.imagesGroupedByLocation.reduce<InspectionImageListItem[]>((acc, group) => acc.concat(group.images), []);
    const fromIndex = flatImages.findIndex((img) => img.id === fromImage.id);
    const toIndex = flatImages.findIndex((img) => img.id === toImage.id);

    if (fromIndex === -1 || toIndex === -1) {
      return;
    }

    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);

    for (let i = start; i <= end; i++) {
      if (!this.isSelectedImage4Select(flatImages[i])) {
        const tempImageIdListItem = new InspectionImageIdListItem();
        tempImageIdListItem.id = flatImages[i].id;
        tempImageIdListItem.locationId = flatImages[i].subjectLocationId;
        tempImageIdListItem.status = flatImages[i].status;
        this.inspDataManageService.selectImageId(tempImageIdListItem);
      }
    }

    this.lastSelectedImage = toImage;
  }

  private groupImagesPerLocation(): void {
    if (this.inspectionImages.length == 0) {
      this.imagesGroupedByLocation = [];
    }

    const list: InspectionImageDisplayRow[] = [];

    // Create a list of display locations
    this.filterTermLocations.forEach((location) => {
      list.push(new InspectionImageDisplayRow(location.identifier, location.name));
    });

    // Store images by location
    this.inspectionImages.forEach((img) => {
      img.usedImageModeControl = new FormControl(img.usedImageMode);

      // If there is no location ID, add it to the unassigned list.
      if (!list.find((_) => _.locationId === img.subjectLocationId) && img.subjectLocationId === null) {
        list.unshift(new InspectionImageDisplayRow(null, this.translate.instant("inspectionDataManagement.unallocated")));
      }

      for (let i = 0; i < list.length; i++) {
        if (list[i].locationId === img.subjectLocationId) {
          list[i].images.push(img);
          break;
        }
      }
    });

    list.forEach((imgList) => {
      imgList.images.sort((a, b) => a?.sortOrder - b?.sortOrder);
    });

    this.oscSpinnerService.hide();
    this.imagesGroupedByLocation = list;
  }

  /**
   * Selected icon display judgment
   * @param  {InspectionImageListItem} inspectionImage
   */
  public isShowSelectedIcon(inspectionImage: InspectionImageListItem): boolean {
    return this.inspectionReport && this.isStillInspectionImage(inspectionImage);
  }

  get havePermissionOnReport(): boolean {
    if (this.inspectionReport) {
      return !(!this.isReportStatusEditing(this.inspectionReport.status) || !this.canEditImage);
    }
    return false;
  }

  openUploadImageDialog() {
    this.uploadInspectionImage.emit();
  }

  openInspectionInfoDialog() {
    this.editInspectionInfo.emit();
  }

  async openExportInspection() {
    // IPLEX One always exports the display name tree; elsewhere the tenant setting decides, legacy by default
    const structureFormat = await this.structureFormatService.resolveFormat();

    if (this.networkStatusService.isOnline) {
      this.dialog.open(CommonImagesShareDialogComponent, {
        height: "500px",
        width: "600px",
        data: { inspectionId: this.inspection.id, structureFormat }
      });
    } else {
      await this.inspectionExportOfflineService.exportInspection(this.inspection.id, structureFormat);
    }
  }

  async updateBrianSynchronizedInspection() {
    const newSyncInspection = this.inspection.id === this.brianSynchronizedInspectionId ? null : this.inspection.id;
    const inspectionTitle = this.inspection.title;
    this.oscSpinnerService.show();
    const syncResult = await this.brianSynchronizedInspectionStateService.setBrianSynchronizedInspectionId(newSyncInspection, inspectionTitle);
    if (syncResult.error) {
      this.snackBar.open(this.translate.instant("inspectionUpsertDialog.brianSynchronizationError"));
    } else {
      if (newSyncInspection) {
        this.snackBar.open(this.translate.instant("inspectionUpsertDialog.brianSynchronizationActivationSuccess"));
        this.fileSysAccessService.executeReturnAppBatFile().subscribe((fileExists) => {
          if (!fileExists) {
            this.snackBar.open(this.translate.instant("desktopMenu.fileNotFound"));
          }
        });
      } else {
        this.snackBar.open(this.translate.instant("inspectionUpsertDialog.brianSynchronizationDeactivationSuccess"));
      }
    }
    this.oscSpinnerService.hide();
  }

  observeLeftContentResize(): void {
    const leftContent = this.elementRef.nativeElement.querySelector(".left-content");
    if (leftContent) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect.height > 38) {
            leftContent.classList.add("tall-left-content");
          } else {
            leftContent.classList.remove("tall-left-content");
          }
        }
      });
      resizeObserver.observe(leftContent);
    }
  }

  get canManageInspection(): boolean {
    return (
      this.userPermission.isAdminRole ||
      this.userPermission.isInspectionOwner ||
      this.userPermission.isInspector ||
      this.networkStatusService.isOnPremise
    );
  }

  public exportCompareImage() {
    const image = this.selectedImages[0];

    this.inspectionImageService
      .getInspectionImage(this.inspection.id, image.id)
      .pipe(
        switchMap((image) => {
          const compareImage: CompareImage = {
            imageName: image.fileName,
            imageUrl: image.filePath,
            isMeasureable: image.isMeasurementEnabled,
            contentType: image.mimeType
          };
          return this.brianExportDataService.exportCompareImageToDevice(compareImage);
        })
      )
      .subscribe({
        complete: () => this.snackBar.open(this.translate.instant("inspectionDataManagement.compareImageSuccessfullySend"))
      });
  }
}
